import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from "../i18n";
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, AlertTriangle, Clock, Play, Loader2,
  ChevronRight, Zap, Eye, Bot, Settings
} from 'lucide-react';
import { api, payrollAPI, companyAPI } from '../services/api';
import { useCurrencyFormatter } from '../hooks/usePayrollFormatters';

const GATE_STYLES = {
  ok:      { color: "#27a644", bg: "rgba(39,166,68,0.08)",   border: "rgba(39,166,68,0.20)"   },
  warning: { color: "#f0a500", bg: "rgba(240,165,0,0.08)",   border: "rgba(240,165,0,0.20)"   },
  blocked: { color: "#e5484d", bg: "rgba(229,72,77,0.08)",   border: "rgba(229,72,77,0.20)"   },
  error:   { color: "#e5484d", bg: "rgba(229,72,77,0.08)",   border: "rgba(229,72,77,0.20)"   },
  skipped: { color: "#62666d", bg: "var(--lm-s2)",           border: "var(--lm-border)"       },
  pending: { color: "#62666d", bg: "var(--lm-s2)",           border: "var(--lm-border)"       },
};

const GATE_ICONS = {
  ok: CheckCircle, warning: AlertTriangle, blocked: XCircle,
  error: XCircle, skipped: Clock, pending: Clock,
};

const GateIcon = ({ gate }) => {
  const style = GATE_STYLES[gate] || GATE_STYLES.pending;
  const Icon = GATE_ICONS[gate] || Clock;
  return <Icon className="h-4 w-4" style={{ color: style.color }} />;
};

// Translate a technical period status (draft/calculated/closed/…) into a
// human-readable label, falling back to the raw value when unmapped.
const periodStatusLabel = (t, status) =>
  t(`payrollClosing.statuses.${String(status || '').toLowerCase()}`, { defaultValue: status || '—' });

// Translate a pipeline gate status (ok/warning/blocked/…) to its label.
const gateLabel = (t, gate) =>
  t(`payrollClosing.gates.${String(gate || '').toLowerCase()}`, { defaultValue: gate || '—' });

const DetailRows = ({ details }) => {
  const { t } = useTranslation();
  if (!details?.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {details.map((detail, index) => (
        <div
          key={`${detail.registration_number || detail.employee || index}`}
          className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-[11px] rounded px-2 py-1.5"
          style={{ color: "var(--lm-ink-3)", backgroundColor: "rgba(255,255,255,0.04)" }}
        >
          <span className="font-medium sm:col-span-2" style={{ color: "var(--lm-ink)" }}>
            {detail.employee || detail}
          </span>
          {detail.overtime_hours !== undefined && <span>{t('payrollClosing.review.overtime')}: {detail.overtime_hours}h</span>}
          {detail.worked_hours !== undefined && <span>{t('payrollClosing.review.worked')}: {detail.worked_hours}h</span>}
          {detail.expected_hours !== undefined && <span>{t('payrollClosing.review.expected')}: {detail.expected_hours}h</span>}
        </div>
      ))}
    </div>
  );
};

const PhaseCard = ({ name, data }) => {
  const { t } = useTranslation();
  const fmtCurrency = useCurrencyFormatter();
  const [open, setOpen] = useState(data?.gate === 'warning' || data?.gate === 'blocked');
  if (!data) return null;
  const style = GATE_STYLES[data.gate] || GATE_STYLES.pending;
  const phaseLabel = t(`payrollClosing.phases.${name}`, { defaultValue: name });
  const gateLabels = {
    ok: t('payrollClosing.gates.ok'), warning: t('payrollClosing.gates.warning'),
    blocked: t('payrollClosing.gates.blocked'), error: t('payrollClosing.gates.error'),
    skipped: t('payrollClosing.gates.skipped'), pending: t('payrollClosing.gates.pending'),
  };

  return (
    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: style.bg, border: `1px solid ${style.border}` }}>
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <GateIcon gate={data.gate} />
          <span className="font-medium text-sm" style={{ color: "var(--lm-ink)" }}>{phaseLabel}</span>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ color: style.color, backgroundColor: style.bg, border: `1px solid ${style.border}` }}
          >
            {gateLabels[data.gate] || data.gate}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs truncate max-w-[260px]" style={{ color: "var(--lm-ink-3)" }}>{data.summary}</span>
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
            style={{ color: "var(--lm-ink-3)" }}
          />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 space-y-2" style={{ borderTop: `1px solid ${style.border}` }}>
          {data.blocking_issues?.map((issue, i) => (
            <div key={i} className="flex items-start gap-2 text-xs rounded p-2"
              style={{ color: "#e5484d", backgroundColor: "rgba(229,72,77,0.08)" }}>
              <XCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "#e5484d" }} />
              <span>{issue.message}</span>
            </div>
          ))}
          {data.warnings?.map((w, i) => (
            <div key={i} className="text-xs rounded p-2"
              style={{ color: "#f0a500", backgroundColor: "rgba(240,165,0,0.08)" }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "#f0a500" }} />
                <span>{w.message}</span>
              </div>
              {w.employees?.length > 0 && (
                <div className="mt-2 pl-6" style={{ color: "var(--lm-ink-3)" }}>
                  {w.employees.join(', ')}
                  {w.count > w.employees.length ? ` +${w.count - w.employees.length}` : ''}
                </div>
              )}
            </div>
          ))}
          {data.flags?.map((f, i) => (
            <div key={i} className="text-xs rounded p-2"
              style={{ color: "#f0a500", backgroundColor: "rgba(240,165,0,0.08)" }}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: "#f0a500" }} />
                <span>{f.message}</span>
              </div>
              <DetailRows details={f.details} />
              {!f.details?.length && f.employees?.length > 0 && (
                <div className="mt-2 pl-6" style={{ color: "var(--lm-ink-3)" }}>
                  {f.employees.join(', ')}
                </div>
              )}
            </div>
          ))}
          {(data.total_processed !== undefined || data.total_entries !== undefined) && (
            <div className="grid grid-cols-3 gap-3 pt-1">
              {data.total_processed !== undefined && (
                <div className="text-center">
                  <p className="text-lg font-semibold" style={{ color: "var(--lm-ink)" }}>{data.total_processed}</p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('payrollClosing.stats.processed')}</p>
                </div>
              )}
              {data.total_calculated !== undefined && (
                <div className="text-center">
                  <p className="text-lg font-semibold" style={{ color: "#27a644" }}>{data.total_calculated}</p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('payrollClosing.stats.calculated')}</p>
                </div>
              )}
              {data.total_errors !== undefined && (
                <div className="text-center">
                  <p className="text-lg font-semibold"
                    style={{ color: data.total_errors > 0 ? "#e5484d" : "var(--lm-ink-3)" }}>
                    {data.total_errors}
                  </p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('payrollClosing.stats.errors')}</p>
                </div>
              )}
              {data.total_net !== undefined && (
                <div className="text-center col-span-2">
                  <p className="text-lg font-semibold" style={{ color: "#5e6ad2" }}>
                    {fmtCurrency(data.total_net)}
                  </p>
                  <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('payrollClosing.stats.netPayroll')}</p>
                </div>
              )}
            </div>
          )}
          {data.error_details?.length > 0 && (
            <div className="space-y-1 mt-1">
              {data.error_details.map((e, i) => (
                <p key={i} className="text-xs rounded px-2 py-1"
                  style={{ color: "#e5484d", backgroundColor: "rgba(229,72,77,0.08)" }}>
                  {e.employee_name}: {e.error}
                </p>
              ))}
            </div>
          )}
          {data.error && (
            <p className="text-xs rounded px-2 py-1"
              style={{ color: "#e5484d", backgroundColor: "rgba(229,72,77,0.08)" }}>
              {data.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const PayrollClosingAgent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [aiConfigured, setAiConfigured] = useState(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [precheck, setPrecheck] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);

  const periodsQ = useQuery({
    queryKey: ['payroll-periods'],
    queryFn: () =>
      payrollAPI.getPayrollPeriods({ limit: 20 }).then(
        (res) => res?.data?.data ?? res?.data?.periods ?? res?.items ?? []
      ),
  });

  const periods = periodsQ.data ?? [];
  const loadingPeriods = periodsQ.isPending;

  useEffect(() => {
    companyAPI.getAISettings()
      .then(r => setAiConfigured(r.data.key_configured && r.data.enabled))
      .catch(() => setAiConfigured(true));
  }, []);

  useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      const draft = periods.find((p) => ['draft', 'calculated'].includes(p.status));
      if (draft) setSelectedPeriodId(String(draft.id));
    }
  }, [periods]);

  const handleCheck = async () => {
    if (!selectedPeriodId) return;
    setLoadingCheck(true); setPrecheck(null); setReport(null);
    try {
      const res = await api.get(`v1/agents/payroll-closing/${selectedPeriodId}/check`);
      setPrecheck(res.data?.check);
    } catch (e) {
      setPrecheck({ gate: 'error', summary: e?.response?.data?.detail || t('payrollClosing.gates.error') });
    } finally { setLoadingCheck(false); }
  };

  const handleRun = async () => {
    if (!selectedPeriodId) return;
    setLoadingRun(true); setReport(null);
    try {
      const res = await api.post(`v1/agents/payroll-closing/${selectedPeriodId}/run`);
      setReport(res.data?.report);
    } catch (e) {
      setReport({ final_status: 'error', error: e?.response?.data?.detail || t('payrollClosing.gates.error') });
    } finally { setLoadingRun(false); }
  };

  if (aiConfigured === null) return null;

  if (aiConfigured === false) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
        <div className="p-4 rounded-full bg-muted">
          <Bot className="h-10 w-10 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">{t('payrollClosing.aiNotConfiguredTitle')}</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t('payrollClosing.aiNotConfiguredDesc')}
          </p>
        </div>
        <button
          onClick={() => navigate('/company-configuration')}
          className="flex items-center gap-2 px-4 py-2 rounded-md border text-sm hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4" />
          {t('payrollClosing.configureAi')}
        </button>
      </div>
    );
  }

  const selectedPeriod = periods.find(p => String(p.id) === selectedPeriodId);
  const canRun = selectedPeriod && ['draft', 'calculated'].includes(selectedPeriod.status?.toLowerCase());
  const phases = report?.phases ? Object.entries(report.phases) : [];

  const cardStyle = { backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", borderRadius: "12px" };
  const inputStyle = {
    width: "100%", backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)",
    color: "var(--lm-ink)", borderRadius: "8px", padding: "8px 12px", fontSize: "14px", outline: "none",
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="p-2.5 rounded-xl flex-shrink-0"
          style={{ background: "linear-gradient(135deg, rgba(94,106,210,0.20) 0%, rgba(94,106,210,0.08) 100%)", border: "1px solid rgba(94,106,210,0.20)" }}
        >
          <Zap className="h-5 w-5" style={{ color: "#5e6ad2" }} />
        </div>
        <div>
          <h2 className="text-xl font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>{t('payrollClosing.title')}</h2>
          <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('payrollClosing.subtitle')}</p>
        </div>
      </div>

      {/* Period selector */}
      <div style={cardStyle}>
        <div className="px-5 pt-4 pb-2">
          <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t('payrollClosing.periodTitle')}</p>
        </div>
        <div className="px-5 pb-5 space-y-4">
          {loadingPeriods ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: "var(--lm-ink-3)" }}>
              <Loader2 className="h-4 w-4 animate-spin" /> {t('payrollClosing.loading')}
            </div>
          ) : (
            <select
              value={selectedPeriodId}
              onChange={e => { setSelectedPeriodId(e.target.value); setPrecheck(null); setReport(null); }}
              style={inputStyle}
            >
              <option value="">{t('payrollClosing.selectPeriod')}</option>
              {periods.map(p => (
                <option key={p.id} value={String(p.id)}>
                  {p.start_date} → {p.end_date}  [{periodStatusLabel(t, p.status)}]
                </option>
              ))}
            </select>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleCheck}
              disabled={!selectedPeriodId || loadingCheck}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: "#5e6ad2", backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.20)" }}
              onMouseEnter={e => { if (!loadingCheck && selectedPeriodId) e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.14)"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.08)"; }}
            >
              {loadingCheck ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              {t('payrollClosing.checkPrereqs')}
            </button>
            <button
              onClick={handleRun}
              disabled={!canRun || loadingRun || loadingCheck}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#5e6ad2", color: "#ffffff", border: "none" }}
              onMouseEnter={e => { if (canRun && !loadingRun) e.currentTarget.style.backgroundColor = "#828fff"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#5e6ad2"; }}
            >
              {loadingRun ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {loadingRun ? t('payrollClosing.runningClosing') : t('payrollClosing.runClosing')}
            </button>
          </div>

          {selectedPeriod && !canRun && (
            <p className="text-xs" style={{ color: "#f0a500" }}>
              {t('payrollClosing.statusWarning', { status: periodStatusLabel(t, selectedPeriod.status) })}
            </p>
          )}
        </div>
      </div>

      {/* Pre-check result */}
      {precheck && !report && (
        <div style={cardStyle}>
          <div className="px-5 pt-4 pb-2 flex items-center gap-2">
            <Eye className="h-4 w-4" style={{ color: "var(--lm-ink-2)" }} />
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t('payrollClosing.precheckTitle')}</p>
          </div>
          <div className="px-5 pb-5">
            <PhaseCard name="prerequisites" data={precheck} />
          </div>
        </div>
      )}

      {/* Full report */}
      {report && (
        <div style={cardStyle}>
          <div className="px-5 pt-4 pb-2 flex items-center justify-between">
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t('payrollClosing.reportTitle')}</p>
            <div className="flex items-center gap-2">
              <GateIcon gate={report.final_status} />
              <span className="text-xs font-semibold"
                style={{ color: GATE_STYLES[report.final_status]?.color || "var(--lm-ink-2)" }}>
                {gateLabel(t, report.final_status)}
              </span>
            </div>
          </div>
          {report.period_label && (
            <p className="px-5 pb-2 text-xs" style={{ color: "var(--lm-ink-3)" }}>{report.period_label} · {report.company}</p>
          )}
          <div className="px-5 pb-5 space-y-2">
            {report.error ? (
              <p className="text-sm rounded p-3" style={{ color: "#e5484d", backgroundColor: "rgba(229,72,77,0.08)" }}>
                {report.error}
              </p>
            ) : (
              phases.map(([name, data]) => <PhaseCard key={name} name={name} data={data} />)
            )}
            {report.completed_at && (
              <p className="text-xs text-right pt-1" style={{ color: "var(--lm-ink-3)" }}>
                {t('payrollClosing.completedAt')} {new Date(report.completed_at).toLocaleString(i18n.language)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollClosingAgent;
